import { motion } from "framer-motion";
import {
  Zap,
  BatteryCharging,
  Droplets,
  Smartphone,
} from "lucide-react";

const AboutUs = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const stagger = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.18,
      },
    },
  };

  return (
    <section id="about-us" style={styles.container}>
      {/* Heading */}
      <motion.div
        style={styles.header}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={stagger}
      >
        <motion.p style={styles.eyebrow} variants={fadeUp}>
          WHY CLUSTERCLEAR
        </motion.p>

        <motion.h2 style={styles.title} variants={fadeUp}>
          Stop losing customers
          <br />
          <span style={styles.titleLight}>before they find you.</span>
        </motion.h2>

        <motion.p style={styles.subtitle} variants={fadeUp}>
          Every second someone spends searching for your Instagram,
          WhatsApp or phone number is friction.
          ClusterClear removes it with one simple tap.
        </motion.p>
      </motion.div>

      {/* Comparison */}
      <motion.div
        style={styles.compareGrid}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        variants={stagger}
      >
        {/* Old Way */}
        <motion.div style={styles.oldCard} variants={fadeUp}>
          <span style={styles.cardLabel}>THE OLD WAY</span>

          <h3 style={styles.cardTitle}>Too many steps.</h3>

          <ul style={styles.list}>
            <li>Search your Instagram username.</li>
            <li>Find the correct account.</li>
            <li>Follow or save your number.</li>
            <li>Message you later... maybe.</li>
          </ul>

          <p style={styles.cardFooter}>You lose attention in seconds.</p>
        </motion.div>

        {/* ClusterClear */}
        <motion.div style={styles.newCard} variants={fadeUp}>
          <span style={styles.cardLabelPurple}>CLUSTERCLEAR</span>

          <div style={styles.iconCircle}>
            <Zap size={34} color="#6355FF" strokeWidth={2} />
          </div>

          <h3 style={styles.cardTitle}>One tap. Everything opens.</h3>

          <p style={styles.cardDescription}>
            Instagram. TikTok. WhatsApp. Website.
            Phone number. Portfolio.
            Everything appears instantly.
          </p>

          <motion.div
            animate={{ scale: [1, 1.03, 1] }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={styles.tapBadge}
            
          >
            Tap once →
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Benefits */}
      <motion.div
        style={styles.features}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={stagger}
      >
        {[
          {
            icon: <BatteryCharging size={28} strokeWidth={1.8} />,
            title: "No charging.",
            text: "No battery. No cables. No setup every morning.",
          },
          {
            icon: <Droplets size={28} strokeWidth={1.8} />,
            title: "Water resistant.",
            text: "Designed to stay on your wrist every day.",
          },
          {
            icon: <Smartphone size={28} strokeWidth={1.8} />,
            title: "Works with iPhone & Android.",
            text: "Anyone can tap your wrist and open your page instantly.",
          },
        ].map((item, index) => (
          <motion.div
            key={index}
            variants={fadeUp}
            whileHover={{
              y: -8,
              transition: { duration: 0.25 },
            }}
            style={styles.featureCard}
          >
            <div style={styles.featureIcon}>{item.icon}</div>

            <h4 style={styles.featureTitle}>{item.title}</h4>

            <p style={styles.featureText}>{item.text}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Bottom statement */}
      <motion.div
        style={styles.statement}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <p style={styles.statementSmall}>Designed for creators, businesses and professionals.</p>

        <h3 style={styles.statementTitle}>
          Wear your business.
          <br />
          Make every introduction effortless.
        </h3>
      </motion.div>
    </section>
  );
};

const styles = {
  container: {
    background: "#ffffff",
    padding: "150px 20px",
    color: "#111111",
  },

  header: {
    textAlign: "center",
    maxWidth: "760px",
    margin: "0 auto",
  },

  eyebrow: {
    fontSize: "12px",
    letterSpacing: "0.18em",
    color: "#777",
    fontWeight: "600",
    marginBottom: "22px",
  },

  title: {
    margin: 0,
    fontSize: "clamp(3rem, 6vw, 5.5rem)",
    lineHeight: 1,
    letterSpacing: "-0.06em",
    fontWeight: "700",
  },

  titleLight: {
    color: "#777",
    fontWeight: "600",
  },

  subtitle: {
    margin: "28px auto 0",
    maxWidth: "580px",
    color: "#666",
    fontSize: "1.05rem",
    lineHeight: "1.7",
  },

  compareGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))",
    gap: "28px",
    maxWidth: "1100px",
    margin: "90px auto 0",
  },

  oldCard: {
    padding: "42px",
    borderRadius: "30px",
    background: "#F7F7F7",
  },

  newCard: {
    padding: "42px",
    borderRadius: "30px",
    background: "#F5F3FF",
    border: "1px solid rgba(99,85,255,0.08)",
  },

  cardLabel: {
    fontSize: "11px",
    letterSpacing: "0.14em",
    color: "#999",
    fontWeight: "600",
  },

  cardLabelPurple: {
    fontSize: "11px",
    letterSpacing: "0.14em",
    color: "#6355FF",
    fontWeight: "700",
  },

  cardTitle: {
    fontSize: "2rem",
    margin: "20px 0",
    lineHeight: "1.1",
    letterSpacing: "-0.04em",
  },

  cardDescription: {
    color: "#555",
    lineHeight: "1.8",
    fontSize: "1rem",
    marginBottom: "34px",
  },

  list: {
    paddingLeft: "18px",
    color: "#666",
    lineHeight: "2",
    marginBottom: "30px",
  },

  cardFooter: {
    color: "#999",
    margin: 0,
  },

  iconCircle: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    background: "#ffffff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "28px",
    marginBottom: "28px",
    boxShadow: "0 12px 35px rgba(0,0,0,0.06)",
  },

  tapBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 18px",
    background: "#6355FF",
    color: "#fff",
    borderRadius: "999px",
    fontWeight: "600",
    fontSize: "14px",
  },

  features: {
    margin: "110px auto 0",
    maxWidth: "1100px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
    gap: "22px",
  },

  featureCard: {
    background: "#FAFAFA",
    padding: "36px",
    borderRadius: "24px",
    border: "1px solid #EEEEEE",
    transition: "all .3s ease",
  },

  featureIcon: {
    width: "58px",
    height: "58px",
    borderRadius: "50%",
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "24px",
    boxShadow: "0 8px 30px rgba(0,0,0,.05)",
  },

  featureTitle: {
    margin: "0 0 12px",
    fontSize: "1.25rem",
    letterSpacing: "-0.02em",
  },

  featureText: {
    margin: 0,
    color: "#666",
    lineHeight: "1.7",
  },

  statement: {
    marginTop: "120px",
    textAlign: "center",
  },

  statementSmall: {
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: ".15em",
    fontSize: "11px",
    marginBottom: "22px",
  },

  statementTitle: {
    margin: 0,
    fontSize: "clamp(2.5rem,5vw,4.5rem)",
    lineHeight: "1.05",
    letterSpacing: "-0.05em",
    fontWeight: "700",
  },
};

export default AboutUs;